import { useEffect, useState } from "react";
import { useBrandsStore } from "@/stores/brands";
import { usePromptsStore } from "@/stores/prompts";
import { useDefaultModelSeed, useModelsStore } from "@/stores/models";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BrandSelector } from "@/components/compare/BrandSelector";
import { ModelSelector } from "@/components/compare/ModelSelector";
import { Badge } from "@/components/ui/badge";
import { LeakWarning } from "@/components/ui/leak-warning";
import { PromptSelect } from "@/components/prompts/PromptSelect";
import { cn } from "@/lib/utils";
import { useBlindQueryLeakCheck } from "@/lib/useBlindQueryLeakCheck";
import type { QueryMode } from "../../../types";

export interface ReportTemplateValues {
  name: string;
  sourceName?: string;
  brand_ids: number[];
  prompt_id: number;
  models: string[];
  runs_per_model: number;
  query_mode: QueryMode;
}

interface CreateReportFormProps {
  onSuccess: (reportId: number) => void;
  onCancel: () => void;
  initialValues?: ReportTemplateValues;
}

export function CreateReportForm({ onSuccess, onCancel, initialValues }: CreateReportFormProps) {
  const fetchBrands = useBrandsStore(s => s.fetch);
  const fetchPrompts = usePromptsStore(s => s.fetch);
  const fetchModels = useModelsStore(s => s.fetch);
  const prompts = usePromptsStore(s => s.prompts);
  const brands = useBrandsStore(s => s.brands);

  const [name, setName] = useState(initialValues?.name ?? "");
  const [selectedBrands, setSelectedBrands] = useState<Set<number>>(() => new Set(initialValues?.brand_ids ?? []));
  const [promptId, setPromptId] = useState(initialValues ? String(initialValues.prompt_id) : "");
  const [selectedModels, setSelectedModels] = useState<Set<string>>(() => new Set(initialValues?.models ?? []));
  const [runsPerModel, setRunsPerModel] = useState(initialValues?.runs_per_model ?? 3);
  const [queryMode, setQueryMode] = useState<QueryMode>(initialValues?.query_mode ?? "standard");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchBrands(); fetchPrompts(); fetchModels(); }, []);

  // Templates ("Use as template") carry their own model list — don't override it.
  useDefaultModelSeed(!initialValues, (ids) => setSelectedModels((prev) => (prev.size > 0 ? prev : new Set(ids))));

  const isBlindQuery = queryMode === "blind_query";
  const minBrands = isBlindQuery ? 0 : 1;
  const selectedPrompt = prompts.find(p => String(p.id) === promptId);
  const selectedBrandNames = brands.filter(b => selectedBrands.has(b.id)).map(b => b.name);
  const autoTrackAllBrands = isBlindQuery && selectedBrands.size === 0;
  const leakCheckBrandNames = autoTrackAllBrands ? brands.map(b => b.name) : selectedBrandNames;
  const { brandNamesInPrompt, hasLeak: hasBlindQueryPromptLeak } = useBlindQueryLeakCheck({
    enabled: isBlindQuery,
    promptTemplate: selectedPrompt?.template,
    brandNames: leakCheckBrandNames,
  });
  const isTemplateFlow = Boolean(initialValues);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("Report name is required"); return; }
    if (isBlindQuery && brands.length === 0) { setError("Add at least one workspace brand to track after each Blind Query response."); return; }
    if (selectedBrands.size < minBrands) { setError(`Select at least ${minBrands} brand${minBrands === 1 ? "" : "s"} to evaluate`); return; }
    if (!promptId) { setError("Select a prompt"); return; }
    if (selectedModels.size < 1) { setError("Select at least 1 model"); return; }
    if (hasBlindQueryPromptLeak) { setError("Remove tracked brand names from the prompt before generating a Blind Query report."); return; }
    setError("");
    setSubmitting(true);

    try {
      const report = await api.reports.create({
        name: name.trim(),
        brand_ids: Array.from(selectedBrands),
        prompt_id: parseInt(promptId),
        models: Array.from(selectedModels),
        runs_per_model: runsPerModel,
        query_mode: queryMode,
      });
      onSuccess(report.id);
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const totalCalls = selectedModels.size * runsPerModel;

  return (
    <Card>
      <CardHeader>
        <CardTitle>New Report</CardTitle>
        <CardDescription>
          Runs the same prompt across every selected model N times, then synthesizes a shareable report. Tweaks create a new generated report and leave existing reports unchanged.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isTemplateFlow && (
          <div className="rounded-lg border border-brand-100 bg-brand-50 px-4 py-3">
            <p className="text-[13px] font-semibold text-brand-950">
              Using {initialValues?.sourceName || "an existing report"} as a template
            </p>
            <p className="mt-1 text-[12px] leading-relaxed text-slate-500">
              Adjust the configuration below to generate a new report. The original report will not change.
            </p>
          </div>
        )}

        <div>
          <Label htmlFor="report-name">Report name</Label>
          <Input
            id="report-name"
            placeholder="e.g. Q2 Law Firm Sentiment Study"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
        </div>

        <div>
          <Label>{isBlindQuery ? "Brands (optional)" : `Brands (min ${minBrands})`}</Label>
          <BrandSelector selected={selectedBrands} onChange={setSelectedBrands} />
          {isBlindQuery && (
            <p className="mt-3 text-[12px] leading-relaxed text-slate-500">
              {autoTrackAllBrands
                ? "All workspace brands will be tracked after each response. None are sent to the model."
                : "Selected tracked brands are monitored after the response. They are not sent to the model."}
            </p>
          )}
        </div>

        <div>
          <Label>Report mode</Label>
          <div className="grid grid-cols-2 gap-2 mt-0.5">
            {([
              { id: "standard" as QueryMode, label: "Standard", description: "Direct brand evaluation." },
              { id: "blind_query" as QueryMode, label: "Blind Query", description: "Unguided prompt, post-response tracking." },
            ]).map(mode => (
              <button
                key={mode.id}
                type="button"
                onClick={() => setQueryMode(mode.id)}
                className={cn(
                  "rounded-md border p-3 text-left transition-all duration-150 cursor-pointer",
                  queryMode === mode.id
                    ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600/20"
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <span className={cn("text-[13px] font-semibold", queryMode === mode.id ? "text-brand-950" : "text-slate-600")}>{mode.label}</span>
                <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{mode.description}</p>
              </button>
            ))}
          </div>
          {isBlindQuery && <Badge className="mt-2">No tracked brands included in prompt</Badge>}
        </div>

        <div>
          <Label htmlFor="report-prompt">Prompt</Label>
          <PromptSelect
            id="report-prompt"
            value={promptId}
            onChange={e => setPromptId(e.target.value)}
            prompts={prompts}
          />
        </div>

        {isBlindQuery && (
          <LeakWarning brandNames={brandNamesInPrompt} context="blind query report" />
        )}

        <div>
          <Label>Models (min 1)</Label>
          <ModelSelector selected={selectedModels} onChange={setSelectedModels} />
        </div>

        <div>
          <Label htmlFor="runs-per-model">Runs per model</Label>
          <Input
            id="runs-per-model"
            type="number"
            min={1}
            max={20}
            value={runsPerModel}
            onChange={e => setRunsPerModel(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
          />
          <p className="text-[11px] text-slate-400 mt-1.5">
            {totalCalls > 0 ? `${totalCalls} total LLM call${totalCalls === 1 ? '' : 's'} will be made, plus 1 synthesis pass.` : 'Pick models to see total call count.'}
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-[13px] text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={handleSubmit} disabled={submitting || hasBlindQueryPromptLeak} title={hasBlindQueryPromptLeak ? "Remove tracked brand names from the prompt before generating a Blind Query report." : undefined}>
            {submitting ? "Starting..." : isTemplateFlow ? "Generate New Report" : "Generate Report"}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
